import classnames from 'classnames';
import { FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import { OperationsBlockRubberTypes } from '../model/operations.types';

import { chakra, Flex, Input, Table, Td, Text, Th, Thead, Tr } from '@chakra-ui-kraud/react';
import { DatePicker, Select } from 'antd';

import { DEFAULT_YEAR_FORMAT, TechCardStatuses, UserRoles, useTechCard } from '@/shared';
import { useAppSelector } from '@/shared/state';

import styles from './custom-operations.module.scss';
import clsx from 'clsx';
import defaultStyles from '../../../tech-card.module.scss';
import { RubberOperationResponse, useGetRubberTechMapByNumberQuery } from '@/shared/state/api/swagger';
import { convertNumberToNumberStringWithDot } from '@/shared/core/utils/convert-string-to-number-string';
import { RubberTechMapFormData } from '@/pages/tech-card-page/form-config-rubber';
import { isChoppingOperation } from '../lib/operations-utils';

export const CustomRubberOperationsBlock: FC<OperationsBlockRubberTypes> = ({
	isEditable,
	employee,
	renderCancelButton,
	renderRenewOperationButton,
	focusedBlock,
	setFocusedBlock,
}) => {
	const cardNumber = Number(useParams().number);
	const { data: cardInfo } = useGetRubberTechMapByNumberQuery(
		{
			rubberTechMapNumber: cardNumber,
		},
		{
			skip: !cardNumber,
		},
	);

	const notDefaultOperations = cardInfo?.rubber_operations?.filter((operation) => !operation.is_default);

	const UserRole = useAppSelector((state) => state.auth.userProfile?.role);
	const { control, setValue, getValues } = useFormContext<RubberTechMapFormData>();

	const { rubber_operations: fields } = getValues();

	const { isCreationMode } = useTechCard();

	const sortedOperationsTypes = fields ? [...fields].sort((a, b) => Number(a?.step_id) - Number(b?.step_id)) : [];

	const isStorekeeperRole = UserRole === UserRoles.storekeeper;

	const canRenew = UserRole !== UserRoles.storekeeper && cardInfo?.status === TechCardStatuses.progress;

	const isFieldEditable = useCallback(
		(index: number) => {
			if (!isEditable || !isStorekeeperRole) {
				return true;
			}
			// Кладовщик может редактировать только дефолтные операции
			const operation = fields?.[index];
			return operation?.is_default === true;
		},
		[isEditable, isStorekeeperRole, fields],
	);

	const customIndex = Number(notDefaultOperations?.length);
	const customOperations = [{}, {}] as RubberTechMapFormData['rubber_operations'];

	// определение завершенной операции (если есть ФИО и дата) и пустого поля
	const isOperationDoneAndEmptyField = (
		operation: RubberOperationResponse,
		fieldValue: string | number | null | undefined,
	) => {
		if (!!operation.fio?.length && !!operation.date?.length && !fieldValue) {
			return true;
		}
		return false;
	};

	const emptyRows = (length: number) => {
		const rows = new Array(length).fill({});
		return rows.map((_, idx) => (
			<Tr key={idx}>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
			</Tr>
		));
	};

	// определение завершенной операции (если есть ФИО и дата)
	const isOperationDone = (operation: RubberOperationResponse) => {
		if (!!operation.fio?.length && !!operation.date?.length) {
			return true;
		}
		return false;
	};

	const handleCalculateWasted = useCallback(
		(row: number) => {
			const countOut = Number(getValues(`rubber_operations.${row}.count_out_number`));
			const countIn = Number(getValues(`rubber_operations.${row}.count_in_number`));

			if (!countOut || Number.isNaN(countOut) || !countIn || Number.isNaN(countIn)) {
				setValue(
					// остаток от (всего штук, грамм - годны штук, грамм) переходит в брак (штук) этой операции
					`rubber_operations.${row}.wasted`,
					undefined,
				);
			} else {
				setValue(
					// остаток от (всего штук, грамм - годны штук, грамм) переходит в брак (штук) этой операции
					`rubber_operations.${row}.wasted`,
					countIn - countOut,
				);
			}
		},
		[getValues, setValue],
	);

	return (
		<Table
			className={clsx(
				focusedBlock?.block === 'operations' &&
					focusedBlock.focusedBy === 'tab' &&
					!focusedBlock.atCurrentBlock &&
					defaultStyles.focused,
				isEditable && styles['edit-table'],
			)}
			onFocus={() => setFocusedBlock && setFocusedBlock('operations')}
		>
			<Thead display="table-header-group">
				<Th rowSpan={2} style={{ textAlign: 'left', verticalAlign: 'middle' }} width="60px">
					<Flex alignItems={'center'} justifyContent={'left'}>
						№ <br />
						опер
					</Flex>
				</Th>
				<Th rowSpan={2} style={{ verticalAlign: 'middle' }} w="400px">
					Наименование операции
				</Th>
				<Th rowSpan={2} style={{ verticalAlign: 'middle' }} w="200px">
					Дата
				</Th>
				<Th rowSpan={2} w="200px" style={{ verticalAlign: 'middle' }}>
					Кол-во, шт.
				</Th>
				<Th rowSpan={2} style={{ verticalAlign: 'middle' }} w="225px">
					Фамилия исполнителя
				</Th>
				<Th rowSpan={2} w="200px" style={{ verticalAlign: 'middle' }}>
					Годных, шт.
				</Th>
				<Th rowSpan={2} style={{ verticalAlign: 'middle' }} w="150px">
					Брак, шт.
				</Th>
				<Th colSpan={2} w="400px">
					Подписи контролирующих операцию
				</Th>
				<Tr>
					<Th style={{ verticalAlign: 'middle' }} w="200px">
						Исполнитель
					</Th>
					<Th verticalAlign="middle !important" w="200px" minW={'194px'}>
						Контролёр ОТК <br /> (Представитель заказчика)
					</Th>
				</Tr>
			</Thead>{' '}
			{!isEditable && !isCreationMode && (
				<>
					{notDefaultOperations?.map((operation) => (
						<Tr key={operation.id} className={operation.is_canceled ? styles['field-disabled'] : ''}>
							<Td style={{ textAlign: 'left' }}>
								{operation.step_id < 10 ? '0' + operation.step_id : operation.step_id}
							</Td>
							<Td style={{ textAlign: 'left', padding: '0px 2px 0px 8px' }}>
								<Text>{`${operation.name} ${operation.is_canceled ? '— операция отменена' : ''}`}</Text>
							</Td>
							<Td>
								{dayjs(operation.date).isValid()
									? dayjs(operation.date).format(DEFAULT_YEAR_FORMAT)
									: ''}
							</Td>
							<Td>
								{!isOperationDoneAndEmptyField(operation, operation.count_in_number)
									? operation.count_in_number
									: '-'}
							</Td>
							<Td>{operation.fio}</Td>
							<Td>
								{!isOperationDoneAndEmptyField(operation, operation.count_out_number)
									? operation.count_out_number
									: '-'}
							</Td>
							<Td>{!operation.wasted ? '' : operation.wasted}</Td>
							<Td></Td>
							<Td></Td>
						</Tr>
					))}
					{emptyRows(2)}
				</>
			)}
			{!isEditable && isCreationMode && sortedOperationsTypes?.length ? (
				<>
					{sortedOperationsTypes?.map((operation) => (
						<Tr key={operation.id} className={operation.is_canceled ? styles['field-disabled'] : ''}>
							<Td style={{ textAlign: 'left' }}>
								{operation.step_id < 10 ? '0' + operation.step_id : operation.step_id}
							</Td>
							<Td style={{ textAlign: 'left' }}>{operation.name}</Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
						</Tr>
					))}
					{emptyRows(2)}
				</>
			) : (
				<></>
			)}
			{isEditable &&
				!isCreationMode &&
				fields?.map((field, index) => {
					if (field.is_default) return null;

					const fieldEditable = !field.is_canceled && isFieldEditable(index);

					return (
						<Tr key={field.id}>
							<Td
								className={field.is_canceled ? styles['field-disabled'] : ''}
								style={{ textAlign: 'left' }}
							>
								{fieldEditable ? (
									<Controller
										name={`rubber_operations.${index}.step_id`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`rubber_operations.${index}.step_id`}
											/>
										)}
									/>
								) : (
									<Text p="6px">{field.step_id}</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								<chakra.div
									w="100%"
									h="100%"
									display="flex"
									justifyContent="space-between"
									alignItems="center"
								>
									{fieldEditable ? (
										<Controller
											name={`rubber_operations.${index}.name`}
											control={control}
											render={({ field: fieldData, fieldState: { error } }) => {
												return (
													<Input
														tabIndex={7}
														isInvalid={!!error}
														{...fieldData}
														h="100%"
														flex="1"
													/>
												);
											}}
										/>
									) : (
										<Text textAlign="left" padding={'8px 1px 8px 6px'} fontWeight="normal">
											{field.name}
										</Text>
									)}
									<Controller
										name={`rubber_operations.${index}.is_canceled`}
										control={control}
										render={() => {
											if (field.is_canceled) {
												return canRenew ? (
													<>{renderRenewOperationButton(field, 'operation')}</>
												) : (
													<></>
												);
											}
											// если в операции нет даты или нет ФИО исполнителя
											if (fieldEditable && (!field.date || !field.fio)) {
												return <>{renderCancelButton(field, 'operation')}</>;
											}
											return <></>;
										}}
									/>
								</chakra.div>
							</Td>
							<Td className={clsx(styles['date-style'], field.is_canceled && styles['field-disabled'])}>
								{fieldEditable ? (
									<Controller
										name={`rubber_operations.${index}.date`}
										control={control}
										render={({ field: { onChange }, fieldState: { error } }) => (
											<DatePicker
												style={{ width: '100%', height: '100%', padding: '0' }}
												tabIndex={7}
												status={error ? 'error' : ''}
												onChange={onChange}
												format={DEFAULT_YEAR_FORMAT}
												defaultValue={
													field.date && dayjs(field.date).isValid()
														? dayjs(field.date)
														: undefined
												}
												name={`rubber_operations.${index}.date`}
											/>
										)}
									/>
								) : (
									<Text fontWeight="normal" textAlign="left">
										{field.date ? dayjs(field.date).format(DEFAULT_YEAR_FORMAT) : ''}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable &&
								!field.is_canceled &&
								(!isOperationDone(field) || UserRole === UserRoles.admin) ? (
									<Controller
										name={`rubber_operations.${index}.count_in_number`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												onChange={(e) => {
													onChange(e);
													handleCalculateWasted(index);
												}}
												isInvalid={!!error}
												value={value}
												name={`rubber_operations.${index}.count_in_number`}
											/>
										)}
									/>
								) : (
									<Text
										p="6px"
										fontWeight="normal"
										textAlign={
											!isOperationDoneAndEmptyField(field, field.count_in_number)
												? 'left'
												: 'center'
										}
									>
										{!isOperationDoneAndEmptyField(field, field.count_in_number)
											? field.count_in_number
											: '-'}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable ? (
									<Controller
										name={`rubber_operations.${index}.fio`}
										control={control}
										render={({ field: { value, onChange } }) => (
											<Select
												tabIndex={7}
												filterOption={(input, option) =>
													(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
												}
												allowClear
												showSearch
												placeholder="Выберите ФИО"
												optionFilterProp="children"
												value={value}
												onChange={(e) => {
													onChange(e);
													setValue(
														`rubber_operations.${index}.fio`,
														e ? employee.find((el) => el.value === e)?.label : '',
													);
												}}
												options={employee}
												style={{ width: '100%' }}
												className={classnames(styles['custom-select'])}
											/>
										)}
									/>
								) : (
									<Text p="6px" fontWeight="normal" textAlign="center">
										{field.fio}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable &&
								!field.is_canceled &&
								(!isOperationDone(field) || UserRole === UserRoles.admin) ? (
									<Controller
										name={`rubber_operations.${index}.count_out_number`}
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error },
											formState: { defaultValues },
										}) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												isInvalid={!!error}
												onChange={(e) => {
													onChange(e);
													// Нужен каст потому что react-hook-form не поддерживает undefined значения для значений инпутов (значение поля не меняется)
													// https://github.com/orgs/react-hook-form/discussions/5858
													let parsedValue: number | undefined = e.target.value
														? Number(convertNumberToNumberStringWithDot(e.target.value))
														: ('' as unknown as undefined);
													parsedValue = Number.isNaN(parsedValue)
														? ('' as unknown as undefined)
														: parsedValue;
													const activeOperations = fields.filter((el) => !el.is_canceled);
													const opIdx = activeOperations.findIndex(
														(el) => el.id === field.id,
													);

													// если это последняя операция перед дефолтными (у дефолтных нельзя менять имена)
													// или если после операции есть только отмененные операции, но есть еще и дефолтные (не отмененные)
													if (opIdx === activeOperations.length - 2) {
														// перенос годных из "Контрольная ПСИ" в  раздел Кол-во "Сдача на СГД"
														// шт., предпоследняя кастомная операция -> первая дефолтная операция
														setValue(
															'default_rubber_operations.0.count_in_number',
															parsedValue,
															{
																shouldValidate: true,
															},
														);
													} else if (opIdx === activeOperations.length - 1) {
														// перенос годных из  "Консервации" в раздел Кол-во "Приёмка на СГД"
														// шт.,последная кастомная операция  -> вторая дефолтная операция
														setValue(
															'default_rubber_operations.1.count_in_number',
															parsedValue,
															{
																shouldValidate: true,
															},
														);
													} else {
														// поиск следующей неотмененной операции
														const currentOperationIndex = fields.findIndex(
															(el) => el.step_id > field.step_id && !el.is_canceled,
														);

														if (
															currentOperationIndex > -1 &&
															!defaultValues?.rubber_operations?.[currentOperationIndex]
																?.count_in_number
														) {
															// если это не последняя операция, расчет годных (штук) в этой операции переходит в грамм, штук следующей НЕ ОТМЕНЕННОЙ операции
															setValue(
																`rubber_operations.${currentOperationIndex}.count_in_number`,
																parsedValue,
															);
														}
													}

													handleCalculateWasted(index);
												}}
												value={value}
												name={`rubber_operations.${index}.count_out_number`}
											/>
										)}
									/>
								) : (
									<Text
										p="6px"
										fontWeight="normal"
										textAlign={
											!isOperationDoneAndEmptyField(field, field.count_out_number)
												? 'left'
												: 'center'
										}
									>
										{!isOperationDoneAndEmptyField(field, field.count_out_number)
											? field.count_out_number
											: '-'}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable ? (
									<Controller
										name={`rubber_operations.${index}.wasted`}
										control={control}
										render={({ field: { onChange, value, name }, fieldState: { error } }) => (
											<Input
												isReadOnly={
													isOperationDone(field)
														? UserRole !== UserRoles.admin
														: isChoppingOperation(field.name)
														? UserRole === UserRoles.operator
														: UserRole === UserRoles.operator ||
														  UserRole === UserRoles.senior_operator
												}
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value && value > 0 ? value : ''}
												name={name}
												className={field.is_canceled ? styles['field-disabled'] : ''}
											/>
										)}
									/>
								) : (
									<Text p="6px" fontWeight="normal" textAlign="left">
										{!field.wasted ? '' : field.wasted}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}></Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}></Td>
						</Tr>
					);
				})}
			{/* Пустые строки в таблице */}
			{isEditable &&
				!isCreationMode &&
				(isStorekeeperRole
					? emptyRows(2)
					: customOperations?.map((field, index) => (
							<Tr key={customIndex + index}>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.step_id`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_rubber_operations.${customIndex + index}.step_id`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.name`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_rubber_operations.${customIndex + index}.name`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.date`}
										control={control}
										render={({ field: { onChange }, fieldState: { error } }) => (
											<DatePicker
												style={{ width: '100%' }}
												tabIndex={7}
												status={error ? 'error' : ''}
												onChange={onChange}
												defaultValue={
													field.date && dayjs(field.date).isValid()
														? dayjs(field.date)
														: undefined
												}
												format={DEFAULT_YEAR_FORMAT}
												name={`custom_rubber_operations.${customIndex + index}.date`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.count_in_number`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												onChange={onChange}
												isInvalid={!!error}
												value={value}
												name={`custom_rubber_operations.${customIndex + index}.count_in_number`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.fio`}
										control={control}
										render={({ field: { value, onChange } }) => (
											<Select
												allowClear
												tabIndex={7}
												filterOption={(input, option) =>
													(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
												}
												showSearch
												placeholder="Выберите ФИО"
												optionFilterProp="children"
												value={value}
												onChange={(e) => {
													onChange(e);
													setValue(
														`custom_rubber_operations.${customIndex + index}.fio`,
														e ? employee.find((el) => el.value === e)?.label : '',
													);
												}}
												options={employee}
												style={{ width: '100%' }}
												className={styles['custom-select']}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.count_out_number`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_rubber_operations.${
													customIndex + index
												}.count_out_number`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_rubber_operations.${customIndex + index}.wasted`}
										control={control}
										render={({ field: { onChange, value, name }, fieldState: { error } }) => (
											<Input
												isReadOnly={
													UserRole === UserRoles.operator ||
													UserRole === UserRoles.senior_operator
												}
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value && value > 0 ? value : ''}
												name={name}
											/>
										)}
									/>
								</Td>
								<Td></Td>
								<Td></Td>
							</Tr>
					  )))}
		</Table>
	);
};
